import React, { useState, useEffect, useMemo } from 'react';
import AuthService from '../services/authService';
import Spinner from './Spinner';

interface AdminCreateUserFormProps {
  onCreateSuccess: () => void;
}

interface Team {
  id: number;
  team_name: string;
}

interface FormErrors {
  username?: string;
  email?: string;
  fullName?: string;
  password?: string;
  confirmPassword?: string;
  teamId?: string;
  designation?: string;
  general?: string;
}

const AdminCreateUserForm: React.FC<AdminCreateUserFormProps> = ({ onCreateSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [teamId, setTeamId] = useState<number | ''>('');
  const [designation, setDesignation] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamNameQuery, setTeamNameQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [success, setSuccess] = useState('');
  
  // Separate loading states
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const authService = new AuthService();

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setIsLoadingTeams(true);
        const response = await authService.getAllTeams();
        if (response.status === 200) {
          const teamsData = response.data || [];
          setTeams(Array.isArray(teamsData) ? teamsData : []);
        } else {
          setErrors({ general: 'Failed to load teams' });
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        setErrors({ general: 'An error occurred while fetching teams' });
      } finally {
        setIsLoadingTeams(false);
      }
    };

    fetchTeams();
  }, []);

  // Function to clear specific field errors
  const clearFieldError = (fieldName: keyof FormErrors) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!username.trim()) {
      newErrors.username = 'Username is required';
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!teamId) {
      newErrors.teamId = 'Team is required';
      isValid = false;
    }

    if (!designation.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Filter teams based on query
  const filteredTeams = useMemo(() => {
    if (!teamNameQuery || !teams || !Array.isArray(teams)) return [];
    return teams.filter((team) => 
      team && typeof team === 'object' && team.team_name && 
      typeof team.team_name === 'string' && 
      team.team_name.toLowerCase().includes(teamNameQuery.toLowerCase())
    );
  }, [teams, teamNameQuery]);

  // Enhanced input handlers with error clearing
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    clearFieldError('username');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    clearFieldError('email');
  };

  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
    clearFieldError('fullName');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    clearFieldError('password');
    // Also clear confirm password error if passwords now match
    if (confirmPassword && e.target.value === confirmPassword) {
      clearFieldError('confirmPassword');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    clearFieldError('confirmPassword');
  };

  const handleDesignationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDesignation(e.target.value);
    clearFieldError('designation');
  };

  const handleTeamSelection = (team: Team) => {
    setSelectedTeam(team);
    setTeamId(team.id);
    setTeamNameQuery('');
    clearFieldError('teamId');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        UserName: username,
        Email: email,
        fullName: fullName,
        password: password,
        designation: designation,
        TeamId: teamId
      };

      const response = await authService.adminCreateUser(payload);

      if (response.status === 201 || response.status === 200) {
        setUsername('');
        setEmail('');
        setFullName('');
        setPassword('');
        setConfirmPassword('');
        setTeamId('');
        setDesignation('');
        setSelectedTeam(null);
        setErrors({});
        onCreateSuccess();
      } else {
        const errorMsg = response?.data?.message || response?.data?.detail || response?.message || 'Failed to create user';
        setErrors({ general: errorMsg });
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Handle validation errors from the backend
      if (error.response?.status === 422 && error.response?.data?.detail) {
        // Extract validation errors from the response
        const validationErrors = error.response.data.detail;
        const formattedErrors: FormErrors = {};
        
        // Process validation errors into a format our form can use
        if (Array.isArray(validationErrors)) {
          validationErrors.forEach(err => {
            // Map backend field names to frontend field names
            const fieldMap: {[key: string]: string} = {
              'UserName': 'username',
              'Email': 'email',
              'FullName': 'fullName',
              'Password': 'password',
              'TeamId': 'teamId',
              'Designation': 'designation'
            };
            
            // Get the field name from the error location or use the field directly
            const fieldName = err.loc?.[1] || err.loc || '';
            const mappedField = fieldMap[fieldName] || fieldName;
            
            // Use the error message as is
            formattedErrors[mappedField as keyof FormErrors] = typeof err.msg === 'string' ? err.msg : String(err);
          });
        } else if (typeof validationErrors === 'string') {
          formattedErrors.general = validationErrors;
        } else {
          formattedErrors.general = 'Validation error occurred';
        }
        
        setErrors(formattedErrors);
      } else {
        // Handle general errors
        setErrors({ 
          general: typeof error.response?.data?.detail === 'string' 
            ? error.response.data.detail 
            : error?.message || 'An unexpected error occurred' 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-6">
        {/* <h2 className="text-2xl font-bold text-gray-800">Create New User</h2> */}
        <p className="text-gray-600">Add a new user to the system</p>
      </div>
      
      {/* Show loading message while teams are being fetched */}
      {isLoadingTeams && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded flex items-center">
          <Spinner className="h-4 w-4 mr-2" />
          Loading teams...
        </div>
      )}
      
      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              id="username"
              type="text"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
              value={username}
              onChange={handleUsernameChange}
              disabled={isSubmitting}
            />
            {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              id="email"
              type="email"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>
        </div>
        
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            id="fullName"
            type="text"
            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.fullName ? 'border-red-500' : 'border-gray-300'}`}
            value={fullName}
            onChange={handleFullNameChange}
            disabled={isSubmitting}
          />
          {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                value={password}
                onChange={handlePasswordChange}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showConfirmPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Team Name Dropdown */}
          <div>
            <label htmlFor="teamSelect" className="block text-sm font-medium text-gray-700 mb-1">
              Select Team *
            </label>
            <div className="relative">
              <div className="flex">
                <input
                  type="text"
                  id="teamSearch"
                  className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search for a team"
                  value={teamNameQuery}
                  onChange={(e) => setTeamNameQuery(e.target.value)}
                  autoComplete="off"
                  disabled={isSubmitting || isLoadingTeams}
                />
                <button 
                  type="button"
                  onClick={() => teamNameQuery ? setTeamNameQuery('') : null}
                  className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
                  disabled={isSubmitting || isLoadingTeams}
                >
                  {teamNameQuery ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {teamNameQuery.length > 0 && !isLoadingTeams && (
                <div className="mt-2 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md z-10 absolute shadow-lg">
                  {!filteredTeams || filteredTeams.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">
                      No teams found
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {filteredTeams.map((team) => (
                        <li 
                          key={team.id}
                          className={`p-2 cursor-pointer hover:bg-blue-50 ${selectedTeam?.id === team.id ? 'bg-blue-100' : ''}`}
                          onClick={() => handleTeamSelection(team)}
                        >
                          <div className="flex items-center">
                            {selectedTeam?.id === team.id && (
                              <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                              </svg>
                            )}
                            <span>{team.team_name}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {selectedTeam && (
              <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                <p className="font-medium text-gray-700">Selected Team:</p>
                <p className="text-blue-600">{selectedTeam.team_name}</p>
              </div>
            )}
            {errors.teamId && <p className="mt-1 text-sm text-red-500">{errors.teamId}</p>}
          </div>

          <div>
            <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
            <input
              id="designation"
              type="text"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.designation ? 'border-red-500' : 'border-gray-300'}`}
              value={designation}
              onChange={handleDesignationChange}
              disabled={isSubmitting}
              placeholder="e.g., Coach, Analyst, Admin"
            />
            {errors.designation && <p className="mt-1 text-xs text-red-500">{errors.designation}</p>}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 flex items-center"
            disabled={isSubmitting || isLoadingTeams}
          >
            {isSubmitting ? (
              <>
                <Spinner className="h-5 w-5 mr-2" />
                <span>Creating...</span>
              </>
            ) : (
              'Create User'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateUserForm;
